// scripts/wav.js

/**
 * Creates a WAV header for the given PCM audio data length
 * @param {number} dataLength - Length of the audio data in bytes
 * @returns {Uint8Array} WAV header
 */
export function createHeader(dataLength) {
  const buffer = new ArrayBuffer(44);
  const view = new DataView(buffer);
  
  // "RIFF" chunk descriptor
  view.setUint32(0, 0x52494646, false); // "RIFF"
  view.setUint32(4, 36 + dataLength, true); // File size
  view.setUint32(8, 0x57415645, false); // "WAVE"
  
  // "fmt " sub-chunk
  view.setUint32(12, 0x666D7420, false); // "fmt "
  view.setUint32(16, 16, true); // Sub-chunk size
  view.setUint16(20, 1, true); // Audio format (PCM)
  view.setUint16(22, 1, true); // Num channels
  view.setUint32(24, 16000, true); // Sample rate
  view.setUint32(28, 16000 * 2, true); // Byte rate
  view.setUint16(32, 2, true); // Block align
  view.setUint16(34, 16, true); // Bits per sample
  
  // "data" sub-chunk
  view.setUint32(36, 0x64617461, false); // "data"
  view.setUint32(40, dataLength, true); // Data size
  
  return new Uint8Array(buffer);
}

/**
* Creates a complete WAV file from Float32Array audio data
* @param {Float32Array} audioData - Raw audio data
* @returns {Uint8Array} Complete WAV file data
*/
export function createWAV(audioData) {
  // Convert Float32Array to Int16Array (16-bit PCM)
  const int16Array = new Int16Array(audioData.length);
  for (let i = 0; i < audioData.length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
  }
  
  // Create WAV file with header
  const wavHeader = createHeader(int16Array.byteLength);
  const wavFile = new Uint8Array(wavHeader.length + int16Array.byteLength);
  wavFile.set(wavHeader);
  wavFile.set(new Uint8Array(int16Array.buffer), wavHeader.length);
  
  return wavFile;
}

/**
* Converts WAV file data to base64 string
* @param {Uint8Array} wavData - WAV file data
* @returns {string} Base64 encoded WAV data
*/
export function toBase64(wavData) {
  const chunks = [];
  const chunkSize = 1024;
  
  for (let i = 0; i < wavData.length; i += chunkSize) {
      const chunk = wavData.slice(i, i + chunkSize);
      chunks.push(String.fromCharCode(...chunk));
  }
  
  return btoa(chunks.join(''));
}

export class WebMToWavConverter {
  constructor() {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }

  async convertWebMToWav(webmBlob) {
    try {
      // Convert WebM blob to ArrayBuffer
      const arrayBuffer = await webmBlob.arrayBuffer();
      
      // Decode the audio data
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      // Create WAV file with 16-bit PCM, 16kHz
      const wavBuffer = this.audioBufferToWav(audioBuffer, {
        sampleRate: 16000,
        float32: false, // Use 16-bit instead of 32-bit float
      });
      
      return new Blob([wavBuffer], { type: 'audio/wav' });
    } catch (error) {
      console.error('Error converting WebM to WAV:', error);
      throw error;
    }
  }

  audioBufferToWav(audioBuffer, options = {}) {
    const sampleRate = options.sampleRate || audioBuffer.sampleRate;
    const float32 = options.float32 || false;

    // Resample if necessary
    let samples;
    if (sampleRate !== audioBuffer.sampleRate) {
      samples = this.resampleAudio(audioBuffer, sampleRate);
    } else {
      samples = audioBuffer.getChannelData(0);
    }

    // Convert to 16-bit PCM
    const wavData = float32 ? samples : this.convertTo16Bit(samples);
    const dataSize = wavData.length * (float32 ? 4 : 2);

    // Create WAV header
    const headerSize = 44;
    const wavBuffer = new ArrayBuffer(headerSize + dataSize);
    const view = new DataView(wavBuffer);

    // Write WAV header
    // "RIFF" chunk descriptor
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    this.writeString(view, 8, 'WAVE');

    // "fmt " sub-chunk
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, float32 ? 3 : 1, true); // audio format (3 = float, 1 = PCM)
    view.setUint16(22, 1, true); // num channels
    view.setUint32(24, sampleRate, true); // sample rate
    view.setUint32(28, sampleRate * (float32 ? 4 : 2), true); // byte rate
    view.setUint16(32, float32 ? 4 : 2, true); // block align
    view.setUint16(34, float32 ? 32 : 16, true); // bits per sample

    // "data" sub-chunk
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // Write audio data
    if (float32) {
      const offset = 44;
      const float32Array = new Float32Array(wavBuffer, offset, wavData.length);
      float32Array.set(wavData);
    } else {
      const offset = 44;
      const int16Array = new Int16Array(wavBuffer, offset, wavData.length);
      int16Array.set(wavData);
    }

    return wavBuffer;
  }

  convertTo16Bit(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16Array;
  }

  resampleAudio(audioBuffer, targetSampleRate) {
    const sourceSampleRate = audioBuffer.sampleRate;
    const sourceData = audioBuffer.getChannelData(0);
    
    const ratio = sourceSampleRate / targetSampleRate;
    const newLength = Math.round(sourceData.length / ratio);
    const result = new Float32Array(newLength);
    
    for (let i = 0; i < newLength; i++) {
      const sourceIndex = Math.floor(i * ratio);
      result[i] = sourceData[sourceIndex];
    }
    
    return result;
  }

  writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
}

export class AudioPlayer {
  constructor() {
    this.audioElement = null;
  }

  async playBlob(blob, onEnded = () => {}) {
    // Clean up previous audio element if it exists
    if (this.audioElement) {
      this.audioElement.pause();
      URL.revokeObjectURL(this.audioElement.src);
    }

    // Create new audio element
    this.audioElement = new Audio();
    this.audioElement.src = URL.createObjectURL(blob);
    this.audioElement.onended = () => {
      URL.revokeObjectURL(this.audioElement.src);
      onEnded();
    };

    try {
      await this.audioElement.play();
      return true;
    } catch (error) {
      console.error('Error playing audio:', error);
      return false;
    }
  }

  stop() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }
}

// Usage example:
/*
// Create instances
const converter = new WebMToWavConverter();
const player = new AudioPlayer();

// Example function to test both formats
async function testPlayback(webmBlob) {
  console.log('Playing WebM version...');
  await player.playBlob(webmBlob, () => {
    console.log('WebM playback finished');
  });

  // Wait for user input or add delay before playing WAV version
  setTimeout(async () => {
    try {
      const wavBlob = await converter.convertWebMToWav(webmBlob);
      console.log('Playing converted WAV version...');
      await player.playBlob(wavBlob, () => {
        console.log('WAV playback finished');
      });
    } catch (error) {
      console.error('Conversion or playback failed:', error);
    }
  }, 1000); // 1 second delay between versions
}

// For quick A/B testing
async function compareFormats(webmBlob) {
  const wavBlob = await converter.convertWebMToWav(webmBlob);
  
  const webmPlayer = new AudioPlayer();
  const wavPlayer = new AudioPlayer();
  
  return {
    playWebm: () => webmPlayer.playBlob(webmBlob),
    playWav: () => wavPlayer.playBlob(wavBlob),
    stopWebm: () => webmPlayer.stop(),
    stopWav: () => wavPlayer.stop()
  };
}
*/

window.toBase64 = toBase64;
window.createWAV = createWAV;
window.createHeader = createHeader;
window.WebMToWavConverter = WebMToWavConverter
window.AudioPlayer = AudioPlayer